import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import type { AssetCreationFlow } from "@runcheetah/shared";

import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@runcheetah/ui";
import { AssetsGallery } from "./brand-details/assets-section";
import { BrandDetailHeader } from "./brand-details/brand-detail-header";
import { BrandOverviewCard } from "./brand-details/brand-overview-card";
import { getAutopilotRuntimeFlag } from "./brand-details/autopilot";
import { FactsTable } from "./brand-details/facts-section";
import { ImageQaSection } from "./brand-details/image-qa-section";
import { LlmCostBreakdownCard } from "./brand-details/llm-cost-section";
import { BrandPagesGallery } from "./brand-details/pages-section";
import { BrandDetailSectionTabs } from "./brand-details/section-tabs";
import type { BrandDetailSection } from "./brand-details/sections";
import { StrategySection } from "./brand-details/strategy-section";
import { VisualUniverseSection } from "./brand-details/visual-universe-section";

const formatDate = formatDateTime;

// ─── BrandDetailsPage ─────────────────────────────────────────────────────────

export function BrandDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [activeSection, setActiveSection] = useState<BrandDetailSection>("overview");
  const [healResult, setHealResult] = useState<string | null>(null);

  const {
    data: brand,
    isPending,
    error,
  } = trpc.admin.brands.get.useQuery({ id: id! }, { enabled: !!id });
  const { data: llmCostBreakdown, isPending: isLlmCostPending } =
    trpc.admin.brands.llmCostBreakdown.useQuery({ brandId: id! }, { enabled: !!id });
  const runtimeFlagsQuery = trpc.admin.runtimeFlags.list.useQuery(
    { target: { scope: "brand", scopeId: id! } },
    { enabled: !!id },
  );

  const deleteMutation = trpc.admin.brands.delete.useMutation({
    onSuccess: () => {
      utils.admin.brands.list.invalidate();
      navigate("/brands");
    },
  });

  const healOnboardingMutation = trpc.admin.brands.healOnboarding.useMutation({
    onSuccess: async (result) => {
      const strategyMessage =
        result.strategyGeneration === "queued"
          ? " Strategy generation queued."
          : result.strategyGeneration === "already_queued"
            ? " Strategy generation was already queued."
            : result.strategyGeneration === "existing_strategy"
              ? " Strategy already exists."
              : "";

      setHealResult(
        result.healed
          ? `Onboarding status healed: ${result.previousStatus} -> ${result.nextStatus}.${strategyMessage}`
          : `Onboarding status is still ${result.nextStatus}.${strategyMessage}`,
      );
      await utils.admin.brands.get.invalidate({ id: id! });
      await utils.admin.brands.list.invalidate();
    },
    onError: (mutationError) => {
      setHealResult(mutationError.message);
    },
  });

  const updateAutopilotMutation = trpc.admin.brands.updateAutopilot.useMutation({
    onSuccess: async () => {
      await utils.admin.runtimeFlags.list.invalidate({
        target: { scope: "brand", scopeId: id! },
      });
    },
  });
  const updateAssetCreationFlowMutation = trpc.admin.brands.updateAssetCreationFlow.useMutation({
    onSuccess: async (result) => {
      utils.admin.brands.get.setData({ id: id! }, (current) =>
        current ? { ...current, assetCreation: result } : current,
      );
      await Promise.all([
        utils.admin.brands.get.invalidate({ id: id! }),
        utils.admin.runtimeFlags.list.invalidate({
          target: { scope: "brand", scopeId: id! },
        }),
      ]);
    },
  });
  const resolveSupportRequestMutation = trpc.admin.brands.resolveSupportRequest.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.admin.brands.get.invalidate({ id: id! }),
        utils.admin.brands.list.invalidate(),
      ]);
    },
  });

  const handleHealOnboarding = () => {
    setHealResult(null);
    healOnboardingMutation.mutate({ id: id! });
  };

  const handleDelete = () => {
    if (
      window.confirm(
        "Are you sure you want to permanently delete this brand and all of its associated data?",
      )
    ) {
      deleteMutation.mutate({ id: id! });
    }
  };

  const autopilot = getAutopilotRuntimeFlag(runtimeFlagsQuery.data);

  const handleToggleAutopilot = () => {
    updateAutopilotMutation.mutate({
      brandId: id!,
      enabled: !autopilot.enabled,
    });
  };

  const handleUpdateAssetCreationFlow = (flow: AssetCreationFlow) => {
    if (flow === brand?.assetCreation.flow) {
      return;
    }

    updateAssetCreationFlowMutation.mutate({
      brandId: id!,
      flow,
    });
  };

  const handleResolveSupportRequest = () => {
    if (!brand?.supportRequest) {
      return;
    }

    resolveSupportRequestMutation.mutate({ supportRequestId: brand.supportRequest.id });
  };

  if (isPending) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Skeleton className="size-8" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-64" />
          <Skeleton className="h-64 md:col-span-2" />
        </div>
      </div>
    );
  }

  if (error || !brand) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-lg font-medium">Failed to load brand</p>
        <p className="text-sm text-muted-foreground">{error?.message}</p>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-6">
      <BrandDetailHeader
        brand={brand}
        healResult={healResult}
        isHealing={healOnboardingMutation.isPending}
        isDeleting={deleteMutation.isPending}
        isResolvingSupportRequest={resolveSupportRequestMutation.isPending}
        onHealOnboarding={handleHealOnboarding}
        onResolveSupportRequest={handleResolveSupportRequest}
        onDelete={handleDelete}
      />

      <BrandDetailSectionTabs activeSection={activeSection} onSectionChange={setActiveSection} />

      {activeSection === "overview" ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.35fr)]">
          <BrandOverviewCard
            brand={brand}
            formatDate={formatDate}
            autopilotEnabled={autopilot.enabled}
            autopilotSource={autopilot.source}
            isAutopilotPending={runtimeFlagsQuery.isPending || updateAutopilotMutation.isPending}
            onToggleAutopilot={handleToggleAutopilot}
            assetCreation={brand.assetCreation}
            pendingAssetCreationFlow={
              updateAssetCreationFlowMutation.isPending
                ? (updateAssetCreationFlowMutation.variables?.flow ?? null)
                : null
            }
            isAssetCreationPending={updateAssetCreationFlowMutation.isPending}
            onAssetCreationFlowChange={handleUpdateAssetCreationFlow}
          />

          <LlmCostBreakdownCard
            brandId={brand.id}
            breakdown={llmCostBreakdown}
            isPending={isLlmCostPending}
            formatDate={formatDate}
          />
        </div>
      ) : null}

      {activeSection === "strategy" ? <StrategySection brandId={brand.id} /> : null}
      {activeSection === "visualUniverse" ? <VisualUniverseSection brandId={brand.id} /> : null}
      {activeSection === "imageQa" ? <ImageQaSection brandId={brand.id} /> : null}
      {activeSection === "facts" ? <FactsTable brandId={brand.id} /> : null}
      {activeSection === "pages" ? <BrandPagesGallery brandId={brand.id} /> : null}
      {activeSection === "assets" ? <AssetsGallery brandId={brand.id} /> : null}
    </div>
  );
}
